import React, { Fragment } from 'react';
import { connect } from '../../redux/store';
import { actions } from '../../redux/modules/surveys';
import { compose, lifecycle, withHandlers, withProps } from 'recompose';
import { I18n, CoreForm } from '@kineticdata/react';
import {
  ErrorNotFound,
  ErrorUnauthorized,
  ErrorUnexpected,
  ErrorMessage,
  LoadingMessage,
  InfoMessage,
} from '@kineticdata/bundle-common';
import { PageTitle } from './PageTitle';
import { parse } from 'query-string';

export const SurveyComponent = ({
  authenticated,
  appLocation,
  isUnsubscribe,
  loading,
  submission,
  submissionError,
  formSlug,
  kappSlug,
  values,
  additionalValues,
  handleCompleted,
}) => (
  <div className="page-container">
    <div className="page-panel">
      <PageTitle
        className="page-title-heading--sm"
        parts={[submission && submission.form.name, 'My Surveys']}
        title={
          isUnsubscribe ? 'Unsubscribe' : submission && submission.form.name
        }
        breadcrumbs={
          !!authenticated
            ? [
                {
                  label: (
                    <span>
                      <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                      <I18n>My Surveys</I18n>
                    </span>
                  ),
                  to: `${appLocation}/my-surveys`,
                },
              ]
            : undefined
        }
      />
      {loading ? (
        <LoadingMessage />
      ) : submissionError ? (
        <ErrorMessage message={submissionError.message} />
      ) : (
        <Fragment>
          <I18n
            context={`kapps.${kappSlug}.forms.${formSlug}`}
            public={!authenticated}
          >
            <div className="embedded-core-form--wrapper">
              {submission ? (
                submission.coreState !== 'Draft' ? (
                  <InfoMessage title="This survey has already been completed." />
                ) : (
                  <CoreForm
                    key={submission.id}
                    submission={submission.id}
                    public={!authenticated}
                    review={submission.coreState !== 'Draft'}
                    form={formSlug}
                    completed={handleCompleted}
                    notFoundComponent={ErrorNotFound}
                    unauthorizedComponent={ErrorUnauthorized}
                    unexpectedErrorComponent={ErrorUnexpected}
                  />
                )
              ) : (
                <CoreForm
                  key={formSlug}
                  public={!authenticated}
                  kapp={kappSlug}
                  form={formSlug}
                  values={{ ...values, ...additionalValues }}
                  completed={handleCompleted}
                  notFoundComponent={ErrorNotFound}
                  unauthorizedComponent={ErrorUnauthorized}
                  unexpectedErrorComponent={ErrorUnexpected}
                />
              )}
            </div>
          </I18n>
        </Fragment>
      )}
    </div>
  </div>
);

const valuesFromQueryParams = queryParams => {
  const params = parse(queryParams);
  return Object.entries(params).reduce((values, [key, value]) => {
    if (key.startsWith('values[')) {
      const vk = key.match(/values\[(.*?)\]/)[1];
      return { ...values, [vk]: value };
    }
    return values;
  }, {});
};

export const handleCompleted = props => response => {
  // Decrement the surveys count
  if (
    typeof props.setHomeSurveysCount === 'function' &&
    typeof props.surveysCount === 'number'
  ) {
    props.setHomeSurveysCount(Math.max(props.surveysCount - 1, 0));
  }
  // Check if either currentPage is null (pre form consolidation) or
  // displayedPage.type is not 'confirmation' (post form-consolidation)
  // to determine that there is no confirmation page and we should redirect.
  if (
    !response.submission.currentPage ||
    (response.submission.displayedPage &&
      response.submission.displayedPage.type !== 'confirmation')
  ) {
    props.navigate(`confirmation`);
  }
};

export const mapStateToProps = (state, props) => ({
  authenticated: state.app.authenticated,
  appLocation: state.app.location,
  kappSlug: state.app.kappSlug,
  loading:
    state.surveyApp.loading ||
    (props.submissionId &&
      !state.surveys.submission &&
      !state.surveys.submissionError),
  submission: state.surveys.submission,
  submissionError: state.surveys.submissionError,
  values: valuesFromQueryParams(state.router.location.search),
  surveysCount: state.app.homeSurveyCount,
  setHomeSurveysCount: state.app.actions.setHomeSurveysCount,
});

export const mapDispatchToProps = {
  fetchSubmission: actions.fetchSubmissionRequest,
  clearSurveyState: actions.clearSurveyState,
};

const enhance = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withProps(props => {
    const isUnsubscribe = !props.submissionId;
    return {
      isUnsubscribe,
      formSlug: !isUnsubscribe ? props.slug : 'survey-opt-out',
      additionalValues: isUnsubscribe ? { 'Survey Slug': props.slug } : {},
    };
  }),
  withHandlers({ handleCompleted }),
  lifecycle({
    componentDidMount() {
      this.props.clearSurveyState();
      this.props.submissionId &&
        this.props.fetchSubmission({
          id: this.props.submissionId,
        });
    },
    componentWillUnmount() {
      this.props.clearSurveyState();
    },
  }),
);

export const Survey = enhance(SurveyComponent);
